/**
 * What state a session is in, and where somebody is allowed to be sent.
 *
 * Both are small and both are security decisions, so both live here where they
 * can be tested without a browser.
 */

export interface SessionLike {
    /** Unix seconds, as Supabase reports it. */
    expires_at?: number | null;
    user?: { id: string; email?: string | null; email_confirmed_at?: string | null } | null;
}

export type SessionState = 'signed-out' | 'active' | 'expired';

/**
 * A session about to expire is treated as expired.
 *
 * The client refreshes tokens on its own, but a laptop that has been asleep
 * comes back with a token that is valid for four more seconds, and the first
 * request fails in whatever way that screen happens to handle. Treating the
 * last minute as expired means the person is asked to sign in, once, instead of
 * watching something fail.
 */
const EXPIRY_GRACE_SECONDS = 60;

export function sessionState(
    session: SessionLike | null | undefined,
    nowMs: number = Date.now(),
): SessionState {
    if (!session || !session.user) return 'signed-out';
    if (session.expires_at == null) return 'active';
    const expiresMs = session.expires_at * 1000;
    return expiresMs - EXPIRY_GRACE_SECONDS * 1000 <= nowMs ? 'expired' : 'active';
}

/** Has this person proved the address is theirs? */
export const isVerified = (session: SessionLike | null | undefined): boolean =>
    Boolean(session?.user?.email_confirmed_at);

/**
 * Whether a session was opened by an emailed link — a sign-up confirmation or a
 * password reset — rather than by the password (Phase 0 hardening;
 * docs/product/phase-0/03 §2).
 *
 * Supabase Auth requires the current password for every password change except
 * in such a session: holding the emailed link is the proof. So the product
 * keeps one only as long as the link's job takes — the reset screen, for the
 * new password — and ends it everywhere else, asking for the password instead.
 * No long-lived session can then change a password without knowing it.
 *
 * Read from the access token's `amr` claim, which Auth signs: the methods the
 * session was authenticated with. A token that cannot be read is not called
 * one; that direction only ever leaves a password-protected session alone.
 *
 * Any method but the password counts, deliberately: the product signs people
 * in with the password only, so today every other method is an emailed link,
 * and a link method Auth adds later is ended too. A new way to sign in (Google,
 * SSO) must be named here, or its sessions will be ended as well.
 */
export function openedByEmailLink(accessToken: string | null | undefined): boolean {
    if (!accessToken) return false;
    try {
        const part = accessToken.split('.')[1] ?? '';
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
        const claims = JSON.parse(atob(base64)) as { amr?: { method?: unknown }[] };
        const methods = (Array.isArray(claims.amr) ? claims.amr : []).map(a => a?.method).filter((m): m is string => typeof m === 'string');
        return methods.length > 0 && !methods.includes('password');
    } catch {
        return false;
    }
}

/* ------------------------------- redirection ------------------------------- */

/**
 * Where we are willing to send somebody after they sign in.
 *
 * `?returnTo=` comes from the address bar, which means it comes from whoever
 * wrote the link. Left unchecked it is an open redirect: a link to our own
 * sign-in page that lands on somebody else's site, wearing our domain in the
 * address bar all the way there. Only a path on this origin is ever honoured,
 * and anything else falls back to the default.
 *
 * `//evil.test` and `/\evil.test` are the two that catch people out: both are
 * protocol-relative and both start with a slash.
 */
export function safeReturnTo(raw: string | null | undefined, fallback = '/'): string {
    const value = (raw ?? '').trim();
    if (!value) return fallback;
    if (!value.startsWith('/')) return fallback;
    // Protocol-relative: "//host" and, because browsers normalise it, "/\host".
    if (/^\/[/\\]/.test(value)) return fallback;
    if (/[ -]/.test(value)) return fallback;
    return value;
}

/**
 * The absolute URL an emailed link should come back to.
 *
 * Built from the app's own configured origin rather than from anything the page
 * was given, so a link in an email can only ever land on this app.
 */
export function callbackUrl(appUrl: string, path: string, returnTo?: string | null): string {
    const base = appUrl.replace(/\/+$/, '');
    const safe = safeReturnTo(returnTo, '');
    const query = safe ? `?returnTo=${encodeURIComponent(safe)}` : '';
    return `${base}${path}${query}`;
}
